import { useEffect, useRef } from "react";
import { api } from "../../lib/api";

/*
 * Impressions: a post counts as viewed once at least half of it has been on
 * screen for a second. Views are batched and sent every few seconds (and when
 * the page is hidden); the server counts one view per viewer per day.
 */

type Source = "feed" | "profile" | "post" | "search" | "community" | "saved";
const pending = new Map<Source, Set<string>>();
const sent = new Set<string>();
let timer: ReturnType<typeof setTimeout> | null = null;

function flush() {
  timer = null;
  for (const [source, ids] of pending) {
    const list = [...ids].slice(0, 50);
    if (!list.length) continue;
    pending.delete(source);
    void api.post("/api/posts/views", { ids: list, source }).catch(() => {});
  }
}

function queue(id: string, source: Source) {
  const key = `${source}:${id}`;
  if (sent.has(key)) return;
  sent.add(key);
  const set = pending.get(source) ?? new Set<string>();
  set.add(id);
  pending.set(source, set);
  if (!timer) timer = setTimeout(flush, 4000);
}

if (typeof document !== "undefined") document.addEventListener("visibilitychange", () => document.visibilityState === "hidden" && flush());

export function useViewTracking<T extends Element>(postId: string, source: Source, enabled = true) {
  const ref = useRef<T | null>(null);
  useEffect(() => {
    const el = ref.current;
    if (!el || !enabled || sent.has(`${source}:${postId}`)) return;
    let t: ReturnType<typeof setTimeout> | null = null;
    const io = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) t ??= setTimeout(() => queue(postId, source), 1000);
        else if (t) {
          clearTimeout(t);
          t = null;
        }
      },
      { threshold: 0.5 },
    );
    io.observe(el);
    return () => {
      io.disconnect();
      if (t) clearTimeout(t);
    };
  }, [postId, source, enabled]);
  return ref;
}
